import { generateStudioAssistantBrief } from "./studioBuilder";
import type { StudioImproveLoopReadModel } from "./studioImproveReadModel";
import type { VetCanStudioReadModel, VetCanStudioStepId } from "./studioReadModel";
import {
  STUDIO_READINESS_SELECTION_DEFINITIONS,
  getEnabledHumanizationLabels,
  getStudioWorkspacePreviewChannelLabel,
  getStudioWorkspaceVoicePersona,
  type StudioWorkspaceState,
} from "./studioWorkspaceState";
import type { StudioGeneratedAssistantBrief } from "./studioTypes";

export const STUDIO_PREMIUM_DEMO_VERSION = "a24.6_v1" as const;

export type StudioPremiumFlowStage = {
  id: VetCanStudioStepId;
  label: string;
  detail: string;
  state: "ready" | "review" | "blocked";
};

export type StudioPremiumVoiceSummary = {
  personaLabel: string;
  personaSummary: string;
  providerMode: string;
  modelLabel: string;
  laneLabel: string;
  channelLabels: string[];
  selectedPreviewChannelLabel: string;
  humanizationLabels: string[];
  routingNote: string;
};

export type StudioPremiumPreviewCheckpoint = {
  title: string;
  summary: string;
  statusLabel: string;
  blockers: string[];
  suggestedNextStep: string;
  previewRoute: string;
};

export type StudioPremiumDeployReadiness = {
  title: string;
  summary: string;
  personaSummary: string[];
  voiceSummary: string[];
  capabilitySummary: string[];
  routingSummary: string[];
  blockers: string[];
  suggestedNextStep: string;
  gate: {
    state: "blocked";
    approvalLabel: string;
    rolloutLabel: string;
    actionLabel: string;
    enabled: false;
  };
};

export type StudioPremiumDemoModel = {
  version: typeof STUDIO_PREMIUM_DEMO_VERSION;
  brief: StudioGeneratedAssistantBrief;
  flowStages: StudioPremiumFlowStage[];
  voiceSummary: StudioPremiumVoiceSummary;
  previewCheckpoint: StudioPremiumPreviewCheckpoint;
  improveLoopSummary: {
    interactionCount: number;
    reviewableSuggestionCount: number;
    blockedSuggestionCount: number;
    pendingDraftCount: number;
    summary: string;
  };
  deployReadiness: StudioPremiumDeployReadiness;
};

function flowStageState(stepState: VetCanStudioReadModel["steps"][number]["state"]) {
  if (stepState === "blocked") return "blocked";
  if (stepState === "draft") return "review";
  return "ready";
}

export function buildStudioPremiumDemoModel(params: {
  studioModel: VetCanStudioReadModel;
  workspaceState: StudioWorkspaceState;
  improveLoopModel: StudioImproveLoopReadModel;
}): StudioPremiumDemoModel {
  const brief = generateStudioAssistantBrief({
    operatorDescription: params.workspaceState.assistantBrief.operatorDescription,
  });
  const persona = getStudioWorkspaceVoicePersona(
    params.workspaceState.voicePersona.selectedPersonaId
  );
  const selectedPreviewChannelLabel = getStudioWorkspacePreviewChannelLabel(
    params.workspaceState.preview.selectedChannel
  );
  const previewRuntimeMapped = Boolean(persona.previewCatalogEntryKey);
  const humanizationLabels = getEnabledHumanizationLabels(
    params.workspaceState.humanization
  );
  const readinessSelections = params.workspaceState.deployment.readinessSelections;
  const pendingReadinessLabels = STUDIO_READINESS_SELECTION_DEFINITIONS.filter(
    (selection) => !readinessSelections[selection.key]
  ).map((selection) => selection.label);
  const readinessComplete = pendingReadinessLabels.length === 0;
  const readinessSummaryLabel = readinessComplete
    ? "All Studio draft acknowledgements are complete."
    : `${pendingReadinessLabels.length} Studio acknowledgement${pendingReadinessLabels.length === 1 ? "" : "s"} still pending.`;
  const enabledHumanizationSummary =
    humanizationLabels.join(", ") || "None selected; manual QA review required";
  const reviewableSuggestionCount = params.improveLoopModel.suggestedEdits.filter(
    (suggestion) => suggestion.reviewState !== "blocked"
  ).length;
  const blockedSuggestionCount = params.improveLoopModel.suggestedEdits.filter(
    (suggestion) => suggestion.reviewState === "blocked"
  ).length;
  const pendingDraftCount = params.improveLoopModel.suggestedEdits.filter(
    (suggestion) => suggestion.reviewState === "pending"
  ).length;

  return {
    version: STUDIO_PREMIUM_DEMO_VERSION,
    brief,
    flowStages: params.studioModel.steps.map((step) => ({
      id: step.id,
      label:
        step.id === "preview"
          ? "Preview and test"
          : step.id === "deploy_readiness"
            ? "Deploy choreography"
            : step.title,
      detail:
        step.id === "preview"
          ? "Preview, rehearse, and monitor before any rollout discussion."
          : step.id === "deploy_readiness"
            ? "Summarize blockers and the next gated action."
            : step.summary,
      state: flowStageState(step.state),
    })),
    voiceSummary: {
      personaLabel: persona.label,
      personaSummary: persona.summary,
      providerMode: "Preview-only ElevenLabs adapter (catalog + env fallback)",
      modelLabel:
        "Mapped preview model when present, otherwise ELEVENLABS_PREVIEW_MODEL_ID or ELEVENLABS_MODEL_ID",
      laneLabel: params.studioModel.previewLane.title,
      channelLabels: params.studioModel.previewLane.channels,
      selectedPreviewChannelLabel,
      humanizationLabels,
      routingNote:
        previewRuntimeMapped
          ? `${selectedPreviewChannelLabel} remains a draft-only workspace preview mode, but this selected persona is mapped to ${persona.previewCatalogEntryLabel} for the internal preview lane only. Live clinic routing is still untouched.`
          : `${selectedPreviewChannelLabel} remains a draft-only workspace preview mode. This selected persona does not have a preview-runtime mapping yet, so preview audio falls back to the current internal preview env binding instead of the draft persona choice.`,
    },
    previewCheckpoint: {
      title: "Preview, test, and monitor",
      summary:
        `Run ${selectedPreviewChannelLabel.toLowerCase()} workspace checks, inspect voice readiness, review deterministic QA friction, and keep ${persona.label} tuning inside the Studio draft lane only.`,
      statusLabel: "Preview-only and gated",
      blockers: [
        "Live clinic deployment remains blocked from Studio.",
        `Selected Studio workspace preview mode: ${selectedPreviewChannelLabel}.`,
        `Humanization focus: ${enabledHumanizationSummary}.`,
        "Owner-confirmed routing truth remains unchanged while testing.",
      ],
      suggestedNextStep:
        readinessComplete
          ? "Use the Preview & Test Center to confirm persona, tone, and readiness before carrying the draft into install-week handoff review."
          : `Use the Preview & Test Center, then complete these acknowledgements: ${pendingReadinessLabels.join(", ")}.`,
      previewRoute: "/studio/preview?returnStep=preview",
    },
    improveLoopSummary: {
      interactionCount: params.improveLoopModel.recentInteractions.length,
      reviewableSuggestionCount,
      blockedSuggestionCount,
      pendingDraftCount,
      summary:
        "Recent previews and conversation samples feed deterministic, reviewable draft suggestions only.",
    },
    deployReadiness: {
      title: "Studio deployment choreography",
      summary: readinessComplete
        ? "Studio records the current persona, preview-channel, and routing acknowledgements while keeping live clinic voice fully gated behind external rollout controls."
        : `Studio still needs explicit operator acknowledgement before install-week handoff review. ${readinessSummaryLabel}`,
      personaSummary: [
        `Assistant: ${brief.assistantName}`,
        `Starter tone: ${brief.starterTone}`,
        `Selected persona: ${persona.label}`,
        `Persona review: ${readinessSelections.personaReviewed ? "acknowledged in the Studio draft" : "still pending operator acknowledgement"}`,
      ],
      voiceSummary: [
        "Voice provider remains preview-only.",
        `Selected Studio workspace preview mode: ${selectedPreviewChannelLabel}`,
        `Humanization toggles: ${enabledHumanizationSummary}`,
        `Current scenario execution lanes stay limited to ${params.studioModel.previewLane.channels.join(", ")}.`,
        previewRuntimeMapped
          ? `Current preview renders can resolve ${persona.label} through ${persona.previewCatalogEntryLabel} inside the preview-only lane.`
          : "Current preview renders fall back to the server preview env binding because this draft persona has no preview-runtime mapping yet.",
      ],
      capabilitySummary: [
        `Recommended: ${brief.recommendedCapabilities
          .map((capability) => capability.label)
          .join(", ")}`,
        `Blocked detections: ${brief.blockedCapabilities
          .filter((capability) => capability.detectedFromInput)
          .map((capability) => capability.label)
          .join(", ") || "None detected in the current builder brief"}`,
        "All capability recommendations stay bounded to the current VetCan-safe front-desk set.",
      ],
      routingSummary: [
        ...params.studioModel.routingTruth.facts,
        `Routing acknowledgment: ${readinessSelections.routingTruthConfirmed ? "confirmed in the Studio draft" : "still pending operator acknowledgement"}`,
        `Preview-only lane acknowledgment: ${readinessSelections.previewLaneConfirmed ? "confirmed in the Studio draft" : "still pending operator acknowledgement"}`,
        "Public-only clinic posture stays intact while Studio remains in draft-and-preview mode.",
      ],
      blockers: [
        ...(!readinessSelections.personaReviewed
          ? ["Persona draft acknowledgement still pending."]
          : []),
        ...(!readinessSelections.routingTruthConfirmed
          ? ["Owner-confirmed routing truth has not been acknowledged in the Studio draft."]
          : []),
        ...(!readinessSelections.previewLaneConfirmed
          ? ["Preview-only lane acknowledgement is still pending in the Studio draft."]
          : []),
        "External rollout approval remains explicit and external to Studio.",
        "Rollout controls remain deterministic and explainable.",
        "Live clinic deployment remains blocked from Studio.",
        ...params.studioModel.liveDeployment.blockers,
      ],
      suggestedNextStep: readinessComplete
        ? "Keep the acknowledged Studio draft in preview-only mode, attach any operator notes to the export package, and wait for explicit rollout approval outside Studio."
        : `Acknowledge ${pendingReadinessLabels.join(", ")} in the Studio draft, then keep the assistant in draft-and-preview mode until explicit rollout approval is in place.`,
      gate: {
        state: "blocked",
        approvalLabel: "External rollout approval required",
        rolloutLabel: "Manifest-only rollout controls",
        actionLabel: "Live clinic deployment blocked",
        enabled: false,
      },
    },
  };
}
